import React from 'react';
import { motion } from 'framer-motion';

const RetirementShoppingLanding = ({ goToInput, theme = "mint" }) => {
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8">
      {/* SEO-optimized heading structure */}
      <h1 className="text-3xl font-bold text-center mb-6 dark:text-white">
        Retirement Shopping Concerns: Secure Your Financial Future
      </h1>
      
      {/* Hero section with main CTA */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-6 mb-8">
        <div className="flex flex-col md:flex-row items-center">
          <div className="mb-6 md:mb-0 md:mr-6 text-center md:text-left">
            <h2 className="text-2xl font-bold mb-3 dark:text-white">Track Your Retirement Spending</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Understand your purchasing habits during retirement and make confident spending decisions that protect your financial future.
            </p>
            <motion.button
              onClick={goToInput}
              className={`px-6 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Track Your Purchase
            </motion.button>
          </div>
          <div className="text-8xl">👵🏼</div>
        </div>
      </div>
      
      {/* Retirement shopping concerns section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Why Retirement Spending Creates Unique Concerns</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">💰</span>
              <div>
                <h3 className="font-bold dark:text-white">Fixed Income Reality</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Living on retirement savings and Social Security means every purchase impacts your long-term financial security.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">📈</span>
              <div>
                <h3 className="font-bold dark:text-white">Inflation Concerns</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Rising costs erode purchasing power, making today's spending decisions even more consequential for tomorrow.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🏥</span>
              <div>
                <h3 className="font-bold dark:text-white">Healthcare Uncertainties</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Balancing everyday purchases against potential future medical expenses creates spending anxiety.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">👨‍👩‍👧‍👦</span>
              <div>
                <h3 className="font-bold dark:text-white">Legacy Considerations</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Wanting to leave something for family members can create feelings of guilt over personal spending.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* How Guilt Gauge helps */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How Our Retirement Spending Tracker Helps</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">1</span>
              Track Spending Habits
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Monitor your retirement purchases and identify patterns that may impact your financial security.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">2</span>
              Calculate Your Guilt Score
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Get an objective assessment of each purchase based on your retirement financial situation.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">3</span>
              Receive Age-Appropriate Advice
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Get tailored recommendations that balance enjoying your retirement with protecting your financial future.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">4</span>
              Set Retirement-Specific Goals
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Create personalized spending plans that help you feel confident about your purchasing decisions.
            </p>
          </div>
        </div>
      </section>
      
      {/* Retirement Shopper Types Section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Discover Your Retirement Shopper Type</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">⚠️</span>
              <h3 className="font-bold mt-2 dark:text-white">The Worrier</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You avoid almost all non-essential purchases out of fear of outliving your savings, potentially missing out on retirement enjoyment.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">💸</span>
              <h3 className="font-bold mt-2 dark:text-white">The Free Spender</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You've worked hard and feel entitled to enjoy retirement without financial constraints, sometimes risking long-term security.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">👨‍👩‍👧‍👦</span>
              <h3 className="font-bold mt-2 dark:text-white">The Legacy Focused</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You prioritize leaving assets for family members, often sacrificing your own quality of life in the process.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">⚖️</span>
              <h3 className="font-bold mt-2 dark:text-white">The Balanced Spender</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You've found a sustainable balance between enjoying retirement and preserving assets, making confident spending decisions.
            </p>
          </div>
        </div>
      </section>
      
      {/* Retirement Financial Statistics */}
      <section className="mb-8 bg-gray-50 dark:bg-gray-700 rounded-xl p-5">
        <h2 className="text-xl font-bold mb-4 dark:text-white">Retirement Spending By The Numbers</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold dark:text-white">71%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">of retirees worry about overspending</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">$4,300</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">average monthly expenses for retirees</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">$377K</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">median retirement savings at age 65</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">13%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">of retirees' spending goes to healthcare</p>
          </div>
        </div>
      </section>
      
      {/* Testimonials section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How Others Have Balanced Retirement Spending</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "After tracking my purchases for 3 months, I realized I was denying myself small pleasures out of fear. Now I have a 'guilt-free' spending budget that gives me confidence."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Robert M., 68</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Former Worrier</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "I used to feel tremendous guilt about every purchase, thinking about what I could leave to my grandchildren. Guilt Gauge helped me find the right balance."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Patricia L., 72</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Balanced Spender</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section - important for SEO */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Common Questions About Retirement Spending</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How much can I safely spend each month in retirement?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              The traditional 4% withdrawal rule is a starting point, but your personal situation matters more. Our tool helps you track spending patterns and determine your sustainable spending level based on your actual habits and needs.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Why do I feel guilty spending money I've saved for retirement?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              This common feeling stems from uncertainty about future needs, inflation concerns, and decades of saving habits. Our guilt calculator helps you objectively assess which purchases are truly worth worrying about versus those you can enjoy guilt-free.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How do I balance leaving an inheritance with enjoying my retirement?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Start by tracking retirement spending with our tool to understand your habits. Then create separate mental accounts: essential spending, personal enjoyment, and legacy funds. This approach helps reduce guilt while protecting inheritance goals.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How do I account for healthcare costs in retirement spending?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Our retirement-specific guilt calculator factors in healthcare considerations. We recommend setting aside a dedicated healthcare emergency fund separate from your regular spending and tracking your purchases with an awareness of potential future needs.
            </p>
          </div>
        </div>
      </section>
      
      {/* CTA section */}
      <section className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 rounded-xl p-6 text-center">
        <h2 className="text-2xl font-bold mb-3 dark:text-white">Enjoy Retirement Without Financial Worry</h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          Start tracking your retirement purchases and find the right balance between enjoyment and security.
        </p>
        <motion.button
          onClick={goToInput}
          className={`px-8 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Calculate Your Retirement Spending Guilt
        </motion.button>
      </section>
      
      {/* Rich SEO footer content */}
      <footer className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">About Retirement Shopping Concerns</h2>
          <p className="mb-3">
            Retirement shopping concerns arise from the unique financial realities faced by seniors living on fixed incomes. The transition from regular paychecks to retirement savings withdrawals creates uncertainty about spending decisions and their long-term impact.
          </p>
          <p className="mb-3">
            Our Retirement Guilt Calculator helps address these concerns by tracking spending patterns, identifying emotional purchase triggers, and providing personalized guidance that balances financial security with quality of life in retirement.
          </p>
          <p>
            Whether you're struggling with anxiety about every purchase or want to ensure your spending aligns with your long-term plans, our free tool helps you develop healthier retirement spending habits that reduce financial stress and increase confidence in your decisions.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default RetirementShoppingLanding;
